import { css } from '@emotion/react'
import { graphql } from 'gatsby'
import { ComponentPropsWithoutRef, useState } from 'react'

import { Seal } from '@/features/brearley-logo'
import { useElementRect } from '@/hooks/useElementRect'
import { absoluteFill, bezier, mq } from '@/theme/mixins'
import { colors } from '@/theme/variables'

import { Curricula } from './Curricula'
import { CurriculumNav } from './CurriculumNav'

interface Props extends ComponentPropsWithoutRef<'section'> {
  curriculumYears?:
    | readonly (Queries.CurriculumYearFragment | null)[]
    | null
}

export const Curriculum = ({
  curriculumYears,
  ...props
}: Props): JSX.Element => {
  const [wrapperRef, setWrapperRef] = useState<HTMLElement | null>(null)
  const { height: wrapperHeight } = useElementRect(wrapperRef)
  const [activeYear, setActiveYear] = useState(curriculumYears?.[0])
  const styles = {
    section: css`
      position: relative;
      display: grid;
      background: #fff;
      grid-template-columns: auto 1fr;
      padding: var(--row-108) var(--margin) var(--row-108);
      grid-column-gap: var(--margin);
      ${mq().s} {
        grid-template-columns: 1fr;
        padding-top: var(--row-72);
        padding-bottom: var(--row-36);
      }
    `,
    curriculumContainer: css`
      position: relative;
      transition: height 500ms ${bezier.easeOut};
      overflow: hidden;
    `,
    curriculumYear: css`
      opacity: 0;
      position: absolute;
      width: 100%;
      top: 0;
      left: 0;
      z-index: 0;
      pointer-events: none;
      transition: opacity 300ms ${bezier.easeOut};
    `,
    masonryContainer: css`
      margin: 0 calc(-0.5 * var(--margin));
    `,
    masonry: css``,
    activeCurriculumYear: css`
      opacity: 1;
      position: relative;
      pointer-events: all;
      z-index: 1;
    `,
    heading: css`
      font-size: var(--fs-60);
      margin: 0 0 1em;
      grid-column: 1 / -1;
      ${mq().s} {
        margin: 0.25em 0 0.75em;
      }
    `,
    sealWrap: css`
      ${absoluteFill};
      overflow: hidden;
    `,
    seal: css`
      opacity: 0.05;
      width: 50vmax;
      height: auto;
      position: absolute;
      right: -12.5vmax;
      top: var(--row-54);
      ${mq().ms} {
        width: 100vw;
        right: -33vw;
      }
    `,
    curriculum: css`
      margin: 0 calc(-0.5 * var(--margin));
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-auto-flow: dense;
      align-items: flex-start;
      ${mq().m} {
        grid-template-columns: 1fr;
      }
    `,
    curriculumColumn: css`
      display: grid;
      ${mq().m} {
        display: contents;
      }
    `,
  }
  return (
    <section
      css={styles.section}
      {...props}
    >
      <div css={styles.sealWrap}>
        <Seal
          color={colors.red}
          css={styles.seal}
        />
      </div>
      <CurriculumNav
        activeYear={activeYear}
        setActiveYear={setActiveYear}
        curriculumYears={curriculumYears}
      />
      <section
        css={styles.curriculumContainer}
        style={{
          height: wrapperHeight + 'px',
        }}
      >
        <div ref={node => setWrapperRef(node)}>
          {curriculumYears?.map((curriculumYear, i) => (
            <div
              key={i}
              css={[
                styles.curriculumYear,
                activeYear === curriculumYear &&
                  styles.activeCurriculumYear,
              ]}
            >
              <h2 css={styles.heading}>
                {curriculumYear?.year} Curriculum
              </h2>
              <div css={styles.curriculum}>
                <div css={styles.curriculumColumn}>
                  {curriculumYear?.curriculum?.map((curricula, i) => {
                    if (!(i % 2)) {
                      return (
                        <Curricula
                          data={curricula}
                          key={curricula?.id}
                          style={{ order: i }}
                        />
                      )
                    }
                  })}
                </div>
                <div css={styles.curriculumColumn}>
                  {curriculumYear?.curriculum?.map((curricula, i) => {
                    if (i % 2) {
                      return (
                        <Curricula
                          data={curricula}
                          key={curricula?.id}
                          style={{ order: i }}
                        />
                      )
                    }
                  })}
                </div>
              </div>
              {
                // order:
                //   i % 2
                //     ? halfTotal + i / 2
                //     : Math.ceil(i / 2),
              }
              {/* <ResponsiveMasonry
                columnsCountBreakPoints={{ 1: 1, 900: 2 }}
                css={styles.curriculum}
              >
                <Masonry>
                  {curriculumYear?.curriculum?.map(curricula => (
                    <Curricula
                      data={curricula}
                      key={curricula?.id}
                      css={styles.curricula}
                    />
                  ))}
                </Masonry>
              </ResponsiveMasonry>*/}
            </div>
          ))}
        </div>
      </section>
    </section>
  )
}

export const CurriculumFragments = graphql`
  fragment CurriculumYear on DatoCmsCurriculumYear {
    __typename
    id: originalId
    year
    curriculum {
      ...Curricula
    }
  }
`
